'use client'

import { DUR, EASE } from '@/animations/settings/vars'
import { useGSAPContext, useSanityFilterSync } from '@/hooks'
import { useListScrollRestore } from '@/hooks/use-list-scroll-restore'
import { cn } from '@/lib/utils'
import {
  Button,
  CardMain,
  Container,
  Section,
  SectionHeaderContent,
  SpotSanityError,
  SpotSanityFilters
} from '@/ui/components'
import {
  getGroupedFacets,
  getMatchedFacets,
  getTitleTag,
  sorting
} from '@/utils'
import { gsap, ScrollTrigger } from '@gsap'
import { memo, useEffect, useRef, useState } from 'react'

import { useStoreEvents, useStoreI18n } from '@/store'
import { Typo3SectionSpotSanityEventList } from '@/types/typo3'
import { Typo3EventItem } from '@/types/typo3/sections/spotsanity-eventlist'

import { toDesignButton } from '@/lib/transformers'
import { Locale } from '@spot'
import { useParams } from 'next/navigation'

const convertToDesignProps = (
  content: Typo3SectionSpotSanityEventList['content']
) => ({
  ...content,
  buttons: content?.buttons?.map((btn) => toDesignButton(btn))
})

interface Props {
  data: Typo3SectionSpotSanityEventList
  variant?: 'bg-green' | 'default'
}

const SpotSanityListEvent = ({ data, variant = 'default' }: Props) => {
  const c = convertToDesignProps(data.content)
  const D = useStoreI18n.use.dictionary()
  const limit = data?.plugin?.limitItems || 9
  const ref = useRef<HTMLElement>(null)
  const eventsRef = useRef<HTMLDivElement>(null)
  const tl = useRef<GSAPTimeline | null>(null)
  const firstAnimSkipRef = useRef(true)
  const linkToPdfBackend = data?.plugin?.linkToPdf
  const params = useParams<{ lang: Locale }>()

  const [animate, setAnimate] = useState(false)
  const [filteredItems, setFilteredItems] = useState<Typo3EventItem[]>([])

  const [delayedItems, setDelayedItems] = useState(filteredItems)
  const didMountRef = useRef(false)

  //stores
  const store = useStoreEvents()
  const {
    facets,
    setFacets,
    setStoreItemsToShow,
    setStoreFilteredItems,
    setStoreFilteredItemsData,
    startDate,
    endDate,
    isTopEvents,
    setIsReady,
    search,
    sortBy,
    reset,
    setStartDate,
    setEndDate,
    setSearch
  } = store

  //animations
  useGSAPContext({
    scope: eventsRef,
    callback() {
      if (!eventsRef.current) {
        return
      }
      tl.current = gsap.timeline({
        paused: true,
        defaults: { ease: EASE.out, duration: DUR.default / 2 }
      })
      tl.current.to(eventsRef.current, { opacity: 0 })
    }
  })

  useEffect(() => {
    // TODO: Check this
    // @ts-expect-error expected error
    const groupedFacets = getGroupedFacets(facets, data.content?.facets)
    const cardDateThreshold = startDate ?? new Date()

    const filtered = data?.content?.items?.filter((item) => {
      const matchesFacets = getMatchedFacets(facets, groupedFacets, item)

      // Check that at least one cleanDates entry overlaps the selected
      // [startDate, endDate] range. Both conditions must be satisfied by
      // the SAME entry, otherwise events outside the range can leak in
      // when one entry matches the start bound and another matches the end.
      const matchesDateRange =
        startDate === null && endDate === null
          ? true
          : item?.cleanDates?.some((date) => {
              if (!date?.startDate || !date?.endDate) {
                return false
              }
              const eventStart = new Date(date.startDate as string)
              const eventEnd = new Date(date.endDate as string)
              eventEnd.setHours(23, 59, 59, 999)

              const rangeStart = startDate !== null ? new Date(startDate) : null
              const rangeEnd = endDate !== null ? new Date(endDate) : null
              rangeEnd?.setHours(23, 59, 59, 999)

              const afterRangeStart =
                rangeStart === null || eventEnd >= rangeStart
              const beforeRangeEnd = rangeEnd === null || eventStart <= rangeEnd

              return afterRangeStart && beforeRangeEnd
            })

      const topEvents = isTopEvents ? item.isTopEvent : true
      const searchMatch =
        search && search.trim() !== ''
          ? item?.title?.toLowerCase().includes(search.toLowerCase()) ||
            item?.summary?.toLowerCase().includes(search.toLowerCase()) ||
            item?.textDates?.some((date) =>
              date?.firstActive?.toLowerCase().includes(search.toLowerCase())
            )
          : true

      // Generate cardDate selecting the first cleanDates date that is
      // after the startDate and formatting by language with day number and short month
      let cardDate: string | undefined = item.cleanDates?.find(
        (date) =>
          new Date(date.startDate + `T${date.startTime}:00`) >=
          cardDateThreshold
      )?.startDate

      cardDate = cardDate ? cardDate : item.cleanDates?.[0]?.startDate
      item.cardDate = cardDate ? new Date(cardDate) : undefined

      return matchesFacets && matchesDateRange && topEvents && searchMatch
    })

    // Sorting
    const sorted = sorting(filtered, sortBy)

    setFilteredItems(sorted as Typo3EventItem[])
  }, [data, facets, startDate, endDate, isTopEvents, search, sortBy])

  useSanityFilterSync({
    data,
    setFacets,
    setSearch,
    setStartDate,
    setEndDate,
    facets,
    startDate,
    endDate,
    search,
    linkToPdfBackend
  })

  // common for all lists
  useEffect(() => {
    if (!didMountRef.current && filteredItems?.length > 0) {
      didMountRef.current = true
      setDelayedItems(filteredItems)
      return
    }

    setStoreFilteredItems(filteredItems?.length)

    const timer = setTimeout(() => setDelayedItems(filteredItems), 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems])

  const handleLoadMore = () => {
    gsap.to(eventsRef.current, {
      duration: DUR.default / 2,
      opacity: 0,
      onComplete: () => {
        setItemsToShow((prev) => Math.min(prev + limit, filteredItems?.length))
        gsap.to(eventsRef.current, { duration: DUR.default / 2, opacity: 1 })
        gsap.delayedCall(0.25, () => ScrollTrigger.refresh())
      }
    })
  }
  const { saveItemsCount, getSavedItemsCount } = useListScrollRestore({
    onLoadMore: handleLoadMore
  })

  const [itemsToShow, setItemsToShow] = useState(() => {
    try {
      const saved = getSavedItemsCount()
      return saved || limit
    } catch {
      return limit
    }
  })

  useEffect(() => {
    // Adjust itemsToShow based on filteredItems length but not more than 10
    setStoreFilteredItems(filteredItems.length)
    setStoreFilteredItemsData(filteredItems)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems.length])

  useEffect(() => {
    // Adjust itemsToShow based on filteredItems length but not more than 10
    setStoreItemsToShow(itemsToShow)
    saveItemsCount(itemsToShow)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsToShow])

  //handlers
  useEffect(() => {
    if (firstAnimSkipRef.current) {
      firstAnimSkipRef.current = false
      return
    }
    setAnimate(true)
    const timer = setTimeout(() => setAnimate(false), 500)
    return () => clearTimeout(timer)
  }, [filteredItems])

  useEffect(() => {
    if (animate) {
      tl.current?.play()
    } else {
      tl.current?.reverse()
    }
  }, [animate])

  //reset all filters if noFilter component is present
  useEffect(() => {
    if (data?.plugin?.hasShowFilter === false) {
      reset()
      setIsReady(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.plugin?.hasShowFilter])

  return (
    <Section
      data={data}
      className={cn(
        's-spotsanity-list-event',
        variant === 'bg-green' && 'bg-accent-300 text-neutral-100'
      )}
      anim="fade-in"
      ref={ref}
    >
      <SectionHeaderContent
        titleLayout={c?.titleLayout}
        as={getTitleTag(c?.titleLayout)}
      >
        {c?.title}
      </SectionHeaderContent>
      <Container>
        {c?.buttons && c?.buttons?.length > 0 && (
          <div className="gap-gutter mt-xs mb-lg flex">
            {c?.buttons.map((button, index) => (
              <Button {...button} key={index?.toString()}>
                {button?.label}
              </Button>
            ))}
          </div>
        )}
        {data.plugin?.hasShowFilter && (
          <SpotSanityFilters data={data} store={useStoreEvents} />
        )}
        {delayedItems.length > 0 && (
          <div
            className={cn(
              'gap-x-xs gap-y-md grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            )}
            ref={eventsRef}
          >
            {delayedItems.slice(0, itemsToShow).map((item) => (
              <CardMain
                variant="event"
                key={item.uid}
                title={item.title}
                link={{ href: item.slug }}
                image={
                  item.images && item.images.length ? item.images[0] : undefined
                }
                location={item.infoLocation || ''}
                cardDate={item.cardDate}
                date={
                  item.textDates?.[0]
                    ? item.textDates[0].isInterval
                      ? item.textDates[0].interval
                      : item.textDates[0].short
                    : ''
                }
              />
            ))}
          </div>
        )}

        <div>
          {delayedItems && itemsToShow < filteredItems?.length && (
            <div className="mt-lg flex justify-center">
              <Button
                variant="secondary"
                onClick={handleLoadMore}
                nextLink={false}
                as="button"
                aria-label={`Load more items. Currently showing ${itemsToShow} of ${filteredItems?.length} items`}
              >
                {D['fe.plugin.filters.loadMore']}
              </Button>
            </div>
          )}
          {delayedItems && delayedItems?.length === 0 && (
            <SpotSanityError store={store} />
          )}
        </div>
      </Container>
    </Section>
  )
}

export default memo(SpotSanityListEvent)
